'use client'

import { ExtendedEvent } from '../types'
import {
  getGameName,
  getGameImageUrl,
  formatTimeRange,
  calculateAvailableSlots,
} from '../utils/event-processor'
import AccessibleEvent from './AccessibleEvent'

interface TimeGridEventProps {
  event: ExtendedEvent
  onClick?: () => void
  onKeyboardNavigation?: (direction: 'up' | 'down' | 'left' | 'right') => void
}

/**
 * Component for rendering events in time grid views (day/week)
 */
export function TimeGridEvent({ event, onClick, onKeyboardNavigation }: TimeGridEventProps) {
  const { extendedProps } = event
  const gameName = getGameName(event)
  const gameImageUrl = getGameImageUrl(event)

  // Format the time range
  const startTime = event.start
    ? typeof event.start === 'string'
      ? event.start
      : event.start instanceof Date
      ? event.start.toISOString()
      : null
    : null

  const endTime = event.end
    ? typeof event.end === 'string'
      ? event.end
      : event.end instanceof Date
      ? event.end.toISOString()
      : null
    : null

  const timeRange = formatTimeRange(startTime, endTime)

  // Calculate player slots
  const maxPlayers = extendedProps?.maxPlayers || 0
  const currentPlayers = extendedProps?.participantCount || 0
  const availableSlots = calculateAvailableSlots(maxPlayers, currentPlayers)

  const renderContent = () => (
    <div className="fc-event-custom-content flex">
      <div className="fc-event-thumbnail-container mr-2 flex-shrink-0">
        {gameImageUrl ? (
          <img
            src={gameImageUrl}
            alt=""
            className="fc-event-thumbnail w-10 h-10 object-cover rounded-md"
            onError={e => {
              console.warn('Image load error, falling back to placeholder')
              e.currentTarget.onerror = null // Prevent infinite loop
              e.currentTarget.src = '/dice-icon.svg'
            }}
            loading="lazy"
            aria-hidden="true"
          />
        ) : (
          <div
            className="fc-event-thumbnail w-10 h-10 flex items-center justify-center bg-gray-700 rounded-md"
            aria-hidden="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="fc-event-title font-medium truncate">{event.title}</div>
        {gameName && (
          <div className="fc-event-game-name text-xs opacity-80 truncate">{gameName}</div>
        )}
        <div className="fc-event-info text-xs">
          <span className="fc-event-time opacity-80">{timeRange}</span>
        </div>
        {maxPlayers > 0 && (
          <div className="fc-event-slots text-xs mt-1">
            <span
              className={`inline-flex px-2 py-0.5 rounded-full ${
                currentPlayers >= maxPlayers
                  ? 'bg-red-900 text-red-200'
                  : currentPlayers >= maxPlayers * 0.8
                  ? 'bg-yellow-700 text-yellow-100'
                  : 'bg-green-800 text-green-100'
              }`}
            >
              {availableSlots} seats remain
            </span>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <AccessibleEvent event={event} onClick={onClick} onKeyboardNavigation={onKeyboardNavigation}>
      {renderContent()}
    </AccessibleEvent>
  )
}
